import * as React from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { SearchBar, SecondaryAlert, SecondaryButton, TertiaryButton } from 'kagr-ui';
import AttributeMenuOption from './attribute-menu-option';
import AttributeMenuCategory from './attribute-menu-category';
import * as styles from './build-segment.module.scss';
import useAttributes from '../../../hooks/useSegmentAttributes';
import { SegmentAttributeBase } from '../../../models/segments';

interface Props {
  open: boolean;
  onApply: (checked: number[]) => void;
  onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '640px'
      }
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  },
  searchProgress: {
    marginRight: 8
  },
  searchBar: {
    width: 200
  }
}));

const AttributeMenu = ({ open, onApply, onCancel }: Props) => {
  const overrides = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const attributesQuery = useAttributes();
  const checked = React.useRef<number[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [searched, setSearched] = React.useState<string>('');
  const [isSearching, setIsSearching] = React.useState<boolean>(false);

  /** Callback passed to attribute checkboxes which track their own checked state */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      checked.current = [...checked.current, Number(event.target.value)];
    } else {
      checked.current = checked.current.filter((id) => id !== Number(event.target.value));
    }
  };

  const handleCancel = () => {
    checked.current = [];
    onCancel();
  };

  const handleApply = () => {
    onApply(checked.current);
    checked.current = [];
  };

  React.useEffect(() => {
    if (searchValue.length >= 3) {
      setIsSearching(true);
    }

    const debounceTimer = setTimeout(() => {
      if (searchValue.length >= 3) {
        setSearched(searchValue);
      } else {
        setSearched('');
      }
      setIsSearching(false);
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchValue]);

  React.useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);

  const checkForTransactional = (categoryAttributes: SegmentAttributeBase[]) => {
    return !!categoryAttributes.find((att) => att.isTransactional === true);
  };

  const content = React.useMemo(() => {
    if (attributesQuery.data) {
      return attributesQuery.data.categories.map((category) => (
        <AttributeMenuCategory
          key={category.category}
          name={category.category}
          forceExpand={searched != ''}
          constainsTransactionalAttribute={checkForTransactional(category.attributes)}
        >
          {category.attributes.map((attribute) => {
            return (
              <AttributeMenuOption
                key={`${attribute.name}-${attribute.id}`}
                id={attribute.id}
                name={attribute.name}
                secondaryText={
                  attribute.populatedPercentage !== undefined ? `${Math.round(attribute.populatedPercentage)}%` : ''
                }
                initialChecked={checked.current.includes(attribute.id)}
                onChange={handleChange}
                hidden={!attribute.name.toLowerCase().includes(searched.toLowerCase())}
                isTransactional={attribute.isTransactional}
              />
            );
          })}
        </AttributeMenuCategory>
      ));
    }
    return null;
  }, [attributesQuery.data, searched]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      scroll="paper"
      fullScreen={fullScreen}
      className={overrides.dialog}
      aria-labelledby="select-attribute-dialog-title"
    >
      <DialogTitle id="select-attribute-dialog-title" className={overrides.dialogTitle} disableTypography>
        <h3 className={styles.dialogTitle}>Select an Attribute</h3>
        <div className={styles.attributeSearch}>
          {isSearching && <CircularProgress size="20px" className={overrides.searchProgress} />}
          <SearchBar
            value={searchValue}
            updateValue={setSearchValue}
            placeholder="Search Attributes"
            allowSubmitButton={false}
            showClearButton
            inputProps={{ disabled: attributesQuery.isLoading }}
            className={overrides.searchBar}
          />
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {attributesQuery.isLoading && (
          <div className={styles.dialogLoading}>
            <CircularProgress size="20px" />
          </div>
        )}
        {attributesQuery.isError && <SecondaryAlert type="error" message={attributesQuery.error.message} />}
        {content}
      </DialogContent>
      <DialogActions className={overrides.dialogActions}>
        <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={handleCancel} />
        <SecondaryButton text="Apply" title="Apply" handleClick={handleApply} />
      </DialogActions>
    </Dialog>
  );
};

export default AttributeMenu;
